// caster-update.ts — handleCasterUpdate caches the new caster_arr, re-paints
// the retained encounter row and refreshes the caster
// sheet if open. ftApplyEncounterCreatureVisualsFromCasterSnapshot is the
// local-call entry point also used by the move-RPC completion path.

import type { WSMessage } from './types';
import type { CasterArr } from '../shared/array-types';
import { ftWarnIfShapeStale, encounterCreatureField, encounterField, itemsCol } from '../shared/array-types';
import { readPlayerCharacterField } from '../janus-engine/display/player-character-field';
import {
    adoptCasterBlobIntoEncounterRow,
    findCasterInEncounterCache,
    fetchAndCacheCaster,
    ftCarryPermissionsForward,
    ftCarryInventoryPermissionsForward,
} from './caster-cache';
import { ftUpdateActivityTokenBars } from './activity-token-bars';
import { casterScalarPaths } from '../shared/caster-view-changes';
import { auraOwnerInstalled } from './aura-owner-state';
import { withAsyncDeadline } from '../shared/async-deadline';
import { beginPlayerFollow, failPlayerFollow, offerPlayerFollow, playerFollowLoading, playerFollowReason, playerFollowOwnedByParent } from './player-follow';
const pendingViewChanges = new Map<string, readonly string[] | undefined>();
window.ftPatchAppliedCasterViews = (changes, rootId) => {
    for (const [id, paths] of changes) {
        if (paths?.length && paths.every(path => path.startsWith('computed.creature.skills.'))) {
            window.ftRerenderPcSheet?.(id, paths);
            continue;
        }
        pendingViewChanges.set(id, paths);
        if (id.toUpperCase() !== rootId.toUpperCase()) repaintCasterFromCache(id);
    }
};

type WSCasterUpdateMessage = Extract<WSMessage, { type: 'caster_update' }>;

/** Last encounter the UI actually applied. The derived getter may already see a newly patched
 * caster-scoped cache row, so stored state must win while detecting a real transition. */
function resolveAppliedActiveEncounterId(): string {
    return String(
        window.FateTide?.activeEncounterId
        || window.ftGetActiveEncounterId?.()
        || '',
    ).trim();
}

const inFlightEncounterDepartureRecoveries = new Map<string, Promise<void>>();

/** User-channel moves reach every campaign tab. Only this page's held party/tree may follow. */
function playerFollowBelongsToPage(casterId: string, source: string, destination: string): boolean {
    const ft = window.FateTide;
    const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
    const row = ft?.casterCache?.get(casterId);
    const page = window as Window & { ftGetCampaignId?: () => unknown;
        ftGetCasterPageEncounterScope?: () => { casterId: string } | null };
    const campaignKey = String(page.ftGetCampaignId?.() || ft?.campaign?.id || '').trim();
    const campaignId = upper(campaignKey);
    if (campaignId) {
        const attached = upper(readPlayerCharacterField(row, 'attached_campaign_id'));
        if (attached) return attached === campaignId;
        const roster = ft?.campaignCache?.get(campaignKey)?.campaign_players;
        if (roster && typeof roster === 'object' && Object.values(roster).some(pc =>
            upper(readPlayerCharacterField(pc, 'item_id') ?? itemsCol(pc, 'item_id')) === upper(casterId))) return true;
    } else if (upper(page.ftGetCasterPageEncounterScope?.()?.casterId) === upper(casterId)) return true;
    const applied = resolveAppliedActiveEncounterId();
    const parent = campaignId || upper(encounterField(ft?.encounterCache?.get(applied), 'encounter_master_parent_id'));
    return [source, destination].some(id => !!id && (upper(id) === upper(applied)
        || !!parent && upper(encounterField(ft?.encounterCache?.get(id), 'encounter_master_parent_id')) === parent));
}

function offerOtherRoomFollow(casterId: string, source: string, destination: string): void {
    if (!playerFollowBelongsToPage(casterId, source, destination)) return;
    offerPlayerFollow(casterId, destination, () => {
        // A PC route is pinned to that PC; Go there explicitly opens the moved character.
        const scope = (window as Window & { ftGetCasterPageEncounterScope?: () => { casterId: string } | null }).ftGetCasterPageEncounterScope?.();
        if (scope && scope.casterId.toUpperCase() !== casterId.toUpperCase()) {
            window.location.assign(`${BASE_URL}item.php?id=${encodeURIComponent(casterId)}`);
        } else {
            const feedback = beginPlayerFollow(casterId, destination, () => offerOtherRoomFollow(casterId, source, destination));
            void withAsyncDeadline(async signal => {
                await window.ftSetActiveCaster?.(casterId);
                signal.throwIfAborted();
                // The campaign frame keeps its initial viewer_id in data-src. Explicitly choosing
                // a different owned PC must carry that PC into the destination's native scene URL.
                const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
                if (frame) {
                    const url = new URL(frame.getAttribute('data-src') || frame.src, window.location.href);
                    url.searchParams.set('viewer_id', casterId);
                    frame.setAttribute('data-src', url.href);
                }
                await recoverActiveViewerCasterAfterEncounterDeparture(source, casterId, destination, true, signal);
            }, 180000).catch(() => failPlayerFollow(feedback, 'player-follow:control-unavailable'));
        }
    });
}

/** A single recovery owner for both identity and source-roster signals. No encounter refetch. */
function recoverActiveViewerCasterAfterEncounterDeparture(
    departedEncounterId: string,
    departedCasterId = '',
    expectedDestination = '',
    explicitFollow = false,
    parentSignal?: AbortSignal,
): Promise<void> {
    if (playerFollowOwnedByParent()) return Promise.resolve();
    const casterId = String(departedCasterId || window.FateTide?.activeViewerCasterId || '').trim();
    const sourceEncounterId = String(departedEncounterId || '').trim();
    const applied = resolveAppliedActiveEncounterId();
    if (!playerFollowBelongsToPage(casterId, sourceEncounterId, expectedDestination)) return Promise.resolve();
    if (window.FateTide?.user?.isGM || !checkIfValidUUID(casterId)) {
        playerFollowReason('player-follow:ineligible-viewer', casterId);
        return Promise.resolve();
    }
    const retry = (): void => { void recoverActiveViewerCasterAfterEncounterDeparture(sourceEncounterId, casterId, expectedDestination, explicitFollow); };
    // A second PC or a deliberately selected room must never be dragged by the first PC's move.
    if (!explicitFollow && applied.toUpperCase() !== sourceEncounterId.toUpperCase()) {
        if (expectedDestination && applied.toUpperCase() !== expectedDestination.toUpperCase()) {
            offerOtherRoomFollow(casterId, sourceEncounterId, expectedDestination);
        } else playerFollowReason('player-follow:source-not-applied', casterId, expectedDestination);
        return Promise.resolve();
    }
    const pending = inFlightEncounterDepartureRecoveries.get(casterId);
    if (pending) return pending;
    const feedback = beginPlayerFollow(casterId, expectedDestination, retry);
    const recovery = (async () => {
        let reason = 'player-follow:stale-read';
        const startedAt = performance.now();
        try {
            for (let attempt = 0; attempt < 3; attempt++) {
                let fresh: CasterArr | undefined;
                let destination = '';
                try {
                    await withAsyncDeadline(async () => {
                        await fetchAndCacheCaster(casterId, { skipEncounterSeed: true, throwOnError: true });
                    }, Math.max(1, 180000 - (performance.now() - startedAt)), parentSignal);
                    fresh = window.FateTide?.casterCache?.get(casterId) as CasterArr | undefined;
                    destination = String(encounterCreatureField(fresh?.encounter_creatures, 'encounter_id') ?? '').trim();
                    reason = 'player-follow:stale-read';
                } catch (error) {
                    reason = error instanceof Error && error.name === 'TimeoutError'
                        ? 'player-follow:read-timeout' : 'player-follow:read-failed';
                }
                if (fresh && checkIfValidUUID(destination) && destination.toUpperCase() !== sourceEncounterId.toUpperCase()) {
                    // Re-check after the await: changing rooms during recovery cancels automatic navigation.
                    const current = resolveAppliedActiveEncounterId();
                    if (current.toUpperCase() !== applied.toUpperCase()
                        && current.toUpperCase() !== destination.toUpperCase()) {
                        offerOtherRoomFollow(casterId, sourceEncounterId, destination);
                        return;
                    }
                    // The cached row is fresh before the setter reads the PC route's encounter scope.
                    applyCasterSideEffects(fresh, casterId, undefined, explicitFollow ? current : sourceEncounterId);
                    if (resolveAppliedActiveEncounterId().toUpperCase() !== destination.toUpperCase()) {
                        window.ftSetActiveEncounterId?.(destination, 'caster-departure');
                        if (typeof ftSyncViewerAmbience === 'function') void ftSyncViewerAmbience();
                        if (typeof window.ftSyncViewerMusic === 'function') void window.ftSyncViewerMusic();
                    }
                    if (resolveAppliedActiveEncounterId().toUpperCase() !== destination.toUpperCase()) {
                        failPlayerFollow(feedback, 'player-follow:scope-refused');
                        return;
                    }
                    playerFollowLoading(feedback, destination);
                    return;
                }
                playerFollowReason(reason, casterId, expectedDestination);
                if (reason === 'player-follow:read-timeout') break;
                if (attempt < 2) await new Promise<void>(resolve => setTimeout(resolve, 750));
            }
            failPlayerFollow(feedback, reason);
        } catch {
            failPlayerFollow(feedback, 'player-follow:apply-failed');
        } finally {
            inFlightEncounterDepartureRecoveries.delete(casterId);
        }
    })();
    inFlightEncounterDepartureRecoveries.set(casterId, recovery);
    return recovery;
}

/**
 * Re-paints an encounter cache creature row from a full caster_arr-shaped snapshot.
 * Used by caster_update websocket and local move RPC completion (mover's optimistic/defer paths).
 */
function ftApplyEncounterCreatureVisualsFromCasterSnapshot(casterArr: CasterArr): void {
    const casterId = encounterCreatureField(casterArr?.encounter_creatures, 'encounter_creature_id');
    if (
        !casterId ||
        typeof checkIfValidUUID !== 'function' ||
        !checkIfValidUUID(casterId)
    ) {
        return;
    }
    const casterEncounterIdForCache = encounterCreatureField(casterArr?.encounter_creatures, 'encounter_id') ?? '';
    if (
        casterEncounterIdForCache &&
        typeof checkIfValidUUID === 'function' &&
        checkIfValidUUID(casterEncounterIdForCache) &&
        window.FateTide?.encounterCache instanceof Map &&
        window.FateTide.encounterCache.has(casterEncounterIdForCache)
    ) {
        const encSnap = window.FateTide.encounterCache.get(casterEncounterIdForCache);
        if (!encSnap) return;
        const crList = Array.isArray(encSnap.encounter_creatures_arr) ? encSnap.encounter_creatures_arr : [];
        for (let i = 0; i < crList.length; i++) {
            const rowId = encounterCreatureField(crList[i]?.encounter_creatures, 'encounter_creature_id');
            if (rowId === casterId) {
                crList[i] = casterArr;
                break;
            }
        }
        encSnap.encounter_creatures_arr = crList;
        window.FateTide.encounterCache.set(casterEncounterIdForCache, encSnap);
    }
}

/**
 * Resolve the casterCache key for a caster_arr snapshot.
 * - In an encounter: the encounter_creature_id (the channel id + pane data-ft-caster-id).
 * - Standalone PC (no encounter creature, e.g. item.php / sidelog open): the PC item_id,
 *   which is the same key the PC-sheet panes mount under and casterCache is seeded with.
 * This mirrors table_item_player_character_inc.php's `$ec_id ?: item_id` and lets a
 * standalone-PC caster_update (delta or full) cache + repaint correctly.
 */
function resolveCasterId(casterArr: CasterArr): string {
    const ecId = encounterCreatureField<string>(casterArr?.encounter_creatures, 'encounter_creature_id');
    if (ecId && checkIfValidUUID(ecId)) return ecId;
    const itemId = itemsCol<string>(casterArr, 'item_id');
    if (itemId && checkIfValidUUID(itemId)) return itemId;
    return ecId ?? '';
}

/**
 * Shared downstream of BOTH the full `caster_update` and the surgical
 * `caster_update_delta` paths: repaint the open info panel + PC-sheet panes from the
 * (freshly-cached or just-patched) blob, re-sync the map token / encounter-cache row,
 * reapply widget visibility / turn controls, and fall back to the legacy full-AJAX
 * caster-sheet refresh only for sheets that haven't flipped data-ft-pc-owns-sheet.
 * Factored out of handleCasterUpdate so the delta handler runs the identical side
 * effects after patching casterCache in place. [[project_surgical_ws_delta]]
 */
function applyCasterSideEffects(casterArr: CasterArr, casterId: string, changedPaths?: readonly string[], previousEncounterId?: string): void {
    const scalar = changedPaths && casterScalarPaths(changedPaths);
    // Encounter id this caster belongs to (caster_arr → encounter_creatures → encounter_id).
    const newEncounterId = String(
        encounterCreatureField(casterArr?.encounter_creatures, 'encounter_id') ?? '',
    ).trim();

    // A caster is keyed by encounter_creature_id throughout the encounter runtime. Player
    // characters obey the same rule because player_characters.item_id is intentionally that same
    // id; it is equality, not a second alias namespace.
    window.FateTide?.casterCache?.set(casterId, casterArr);
    if (!changedPaths || changedPaths.some(path => path === 'statuses' || path.startsWith('statuses.')
        || /^computed\.vitality(\.|$)|^encounter_creatures\.(v_current|v_temp|v_max|v_max_temp|encounter_creature_dead|encounter_creature_deleted)$/.test(path)))
        auraOwnerInstalled(newEncounterId, [casterId]);

    // Live-refresh the open side info panel (essence / health / AC / luck-fortune /
    // mana-spirit) straight from the cached blob — no full PHP re-query. Defined in the
    // info-panel bundle; optional-call so order/availability is never a hard dependency.
    // [[project_info_panel_bundle]]
    window.ftRerenderOpenInfoPanel?.(casterId);

    // Surgically repaint client-rendered PC-sheet panes from the cached blob — no
    // full-sheet AJAX, so scroll / open sub-tab / focus survive. Returns true when a TS
    // PC-sheet pane is on screen for this caster. [[PC sheet → TS]]
    const tsPcSheetPresent = window.ftRerenderPcSheet?.(casterId, changedPaths) === true;

    // A caster delta is authoritative even when this encounter-managed viewer receives no separate
    // caster/full-encounter frame. Publish the canonical row explicitly so route-owned encounter
    // projections (FT3D resource cards) can merge it into their held encounterArr and repaint now.
    if (typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
        window.dispatchEvent(new CustomEvent('ft:caster-state-applied', {
            detail: { casterId, encounterId: newEncounterId, casterArr },
        }));
    }

    // Summons pill strip: NOT a .ft-pc-sheet-pane (built only by the full card render), so the
    // surgical pane pass above never touches it. The server pushes the CONTROLLER onto
    // affected_ids on summon spawn/delete, so this caster's fresh blob carries the new baked
    // caster_summons roster — repaint the strip in place (add/remove pills, keep loaded
    // sub-sheets). Cheap no-op when no strip for this caster is on screen or the roster is
    // unchanged. (Residual of plans/completed/summons-ts-pane-and-websocket-push.md.)
    if (!scalar) try { window.ftRerenderSummonsStrip?.(casterId); } catch (e) { /* non-fatal */ }

    // Class-mechanic charge bars (icon + slider + current/max) at the top of the abilities tab: like
    // the summons strip, these live in the abilities-tab SHELL, not a .ft-pc-sheet-pane, so the
    // surgical pane pass above never repaints them. A charge spent via the weapons-tab ± strip (which
    // DOES repaint, being a pane), an ability, or a rest left this slider stale until reopen — repaint
    // it in place from the fresh blob. Cheap no-op when no bar host for this caster is on screen.
    if (!scalar) try { window.ftRerenderClassMechanicBars?.(casterId); } catch (e) { /* non-fatal */ }

    // Re-hydrate the Core Abilities special-actions strip (Fortunate / End Concentration / Stop
    // Burning / Exit Cover / End Hunker / Revert Shapeshift) from the freshly-cached blob. That
    // strip is a ONE-SHOT island mounted by core_abilities_inc.php and the TS player/creature card
    // (#ft_special_actions_{main|ec}_*); it is NOT a .ft-pc-sheet-pane, so ftRerenderPcSheet above
    // never touches it. Without this, ending concentration (or stopping burning, etc.) left the
    // stale "End Concentration" button on screen until a full sheet re-open — the castable-spells
    // End Concentration toolbar already repaints because it rides the castable_spells pane.
    // (owner-reported 2026-06-23). The host id encodes the sanitized caster id; main_ uses the PC
    // item_id, ec_ the encounter_creature_id — casterId here is exactly that resolved id.
    try {
        if ((!scalar || changedPaths?.some(path => /\.(luck|fortune)$/.test(path))) && typeof window.renderCasterSpecialActionsInline === 'function') {
            const san = String(casterId).replace(/[^A-Za-z0-9_]/g, '');
            const mainHost = document.getElementById(`ft_special_actions_main_${san}`);
            if (mainHost) void window.renderCasterSpecialActionsInline(casterId, mainHost.id, false);
            const ecHost = document.getElementById(`ft_special_actions_ec_${san}`);
            if (ecHost) void window.renderCasterSpecialActionsInline(casterId, ecHost.id, true);
        }
    } catch (e) { /* non-fatal */ }

    // If the "Modify Creature Status Effects" form is open in the sidelog for this caster, repaint its
    // current-status list from the refreshed cache so a delete / edit drops/updates the row in the form
    // too (not just on the card's Status Effects tab). No-op when no such form is open. [[status-effect bundle]]
    if (!scalar) window.ftRerenderCreatureStatusList?.(casterId);

    // Twin for the INVENTORY status form (same shared renderer): a status added/removed on an item this
    // caster carries pushes a caster_update. Only pay the whole-blob inventory-id walk when an inventory
    // status list is actually open AND this caster carries that exact item — bounded on the combat hot path.
    try {
        const openList = scalar ? null : document.querySelector('[data-ft-inventory-status-list]') as HTMLElement | null;
        if (openList) {
            const openInvId = (openList.getAttribute('data-ft-inventory-status-list') || '').toUpperCase();
            const invIds = typeof window.ftCollectInventoryIds === 'function'
                ? window.ftCollectInventoryIds(casterArr)
                : [];
            if (openInvId && invIds.some((id) => String(id).toUpperCase() === openInvId)) {
                window.ftRerenderInventoryStatusList?.(openInvId);
            }
        }
    } catch (e) { /* non-fatal */ }

    ftApplyEncounterCreatureVisualsFromCasterSnapshot(casterArr);

    // Refresh the above-token timed-activity bar (gathering/reading/training) — caster_update has just
    // patched the widget.encounterArr row this token reads, so the bar appears on activity start, updates,
    // and is removed on finish. No-op when this caster has no map token / no active activity.
    if (!scalar) ftUpdateActivityTokenBars();

    // ------------------------------------------------------------------
    // Detect encounter change for the active viewer caster
    // ------------------------------------------------------------------
    // Compare against the STORED active id, not the derived getter. On a caster-scoped player
    // sheet ftGetActiveEncounterId() intentionally resolves straight from the freshly-patched
    // casterCache, so it already returns newEncounterId at this point; comparing against it would
    // hide the real old → new transition and skip the setter/event that rebuilds the map + rotates
    // encounter subscriptions. The stored value is the last encounter the UI actually applied.
    const currentEncounterId = resolveAppliedActiveEncounterId();

    if (!playerFollowOwnedByParent() && !window.FateTide?.user?.isGM && window.FateTide?.activeViewerCasterId === casterId
        && previousEncounterId !== undefined && newEncounterId && newEncounterId.toUpperCase() !== previousEncounterId.toUpperCase()
        && previousEncounterId.toUpperCase() !== currentEncounterId.toUpperCase()
        && newEncounterId.toUpperCase() !== currentEncounterId.toUpperCase()) {
        offerOtherRoomFollow(casterId, previousEncounterId, newEncounterId);
    }
    if (
        !playerFollowOwnedByParent() && (window.FateTide?.user?.isGM
            || previousEncounterId?.toUpperCase() === currentEncounterId.toUpperCase()
                && playerFollowBelongsToPage(casterId, previousEncounterId, newEncounterId)) &&
        window.FateTide?.activeViewerCasterId === casterId && // Only act if this is the caster we're viewing
        newEncounterId &&
        checkIfValidUUID(newEncounterId) &&
        newEncounterId !== currentEncounterId
    ) {
        ftDebugLog('caster', { encounterSwitch: { from: currentEncounterId, to: newEncounterId } });

        const follow = window.FateTide?.user?.isGM ? undefined : beginPlayerFollow(casterId, newEncounterId,
            () => { void recoverActiveViewerCasterAfterEncounterDeparture(currentEncounterId, casterId, newEncounterId); });
        window.ftSetActiveEncounterId?.(newEncounterId, 'caster-update');
        if (follow) playerFollowLoading(follow, newEncounterId);

        // Re-scope ambience to the encounter this viewer just moved into (e.g. through an encounter link).
        // The active encounter cache context is now the new encounter, so ftSyncViewerAmbience resolves THIS encounter's track
        // (weather/night/default) and plays it — the move path otherwise never touched audio, so a player
        // kept hearing the GM's loaded-encounter loop. No-op for the GM / on-loaded-encounter viewers.
        if (typeof ftSyncViewerAmbience === 'function') {
            void ftSyncViewerAmbience();
        }
        // Same reasoning for music: the new encounter has its own bucket, and without this the
        // player would keep the previous room's playlist running after the move.
        if (typeof window.ftSyncViewerMusic === 'function') {
            void window.ftSyncViewerMusic();
        }
    }

    // ------------------------------------------------------------------
    // Refresh the caster sheet if it's open for this creature.
    // - NPC / creature sheet: hidden input.ft-input-active-encounter-caster (table_encounter_creature_inc.php)
    // - Player character sheet: input#input_active_caster_sheet or input_active_caster_sheet_ec_* only;
    //   PCs never had ft-input-active-encounter-caster, so inventory never refreshed on caster_update.
    // ------------------------------------------------------------------
    const casterIdEsc = String(casterId).replace(/"/g, '');
    let sheetOpenForThisCaster: Element | null = document.querySelector(
        'input.ft-input-active-encounter-caster[value="' + casterIdEsc + '"]'
    );
    if (!sheetOpenForThisCaster) {
        const hiddenSheetInputs = document.querySelectorAll<HTMLInputElement>(
            'input[type="hidden"][id^="input_active_caster_sheet"]'
        );
        for (let i = 0; i < hiddenSheetInputs.length; i++) {
            if (hiddenSheetInputs[i]!.value === casterId) {
                sheetOpenForThisCaster = hiddenSheetInputs[i]!;
                break;
            }
        }
    }
    if (sheetOpenForThisCaster) {
        // Once the PC sheet is fully TS-owned it sets data-ft-pc-owns-sheet="1"; from
        // then the surgical pane re-render above is sufficient and we skip the legacy
        // full PHP/AJAX refresh. During the per-tab migration (un-ported tabs still
        // PHP-rendered) no element sets that flag, so the legacy refresh still runs —
        // zero regression, while the ported panes already update surgically. [[PC sheet → TS]]
        const ownsSheet = !!document.querySelector(
            '[data-ft-pc-owns-sheet="1"][data-ft-caster-id="' + casterIdEsc + '"]',
        );
        if (!(tsPcSheetPresent && ownsSheet)) {
            refreshCasterSheet(casterId);
        }
    }
}

/**
 * Run the FULL caster_update side effects from the already-cached blob for `casterId` — i.e. repaint
 * the sheet panes / map token / widgets AND the open side-info drawer card, exactly as handleCasterUpdate
 * does, but WITHOUT replacing the cache (the caller has already patched it in place). This is the local
 * "I just edited something, reflect it now from cache" entry point — e.g. the optimistic repaint after an
 * inventory save (window.ftApplyInventoryEditToCache), so the side-info card + sheet inventory tile update
 * instantly instead of waiting on the websocket round-trip (which still arrives later and reconciles).
 */
function repaintCasterFromCache(casterId: string): void {
    if (!casterId || typeof checkIfValidUUID !== 'function' || !checkIfValidUUID(casterId)) return;
    const changedPaths = pendingViewChanges.get(casterId);
    pendingViewChanges.delete(casterId);
    const blob = window.FateTide?.casterCache?.get(casterId);
    if (!blob) return;
    applyCasterSideEffects(blob, casterId, changedPaths);
    try {
        if (
            typeof window.ftRerenderOpenSideInfo === 'function'
        ) {
            const invIds = (!changedPaths || !casterScalarPaths(changedPaths)) && typeof window.ftCollectInventoryIds === 'function'
                ? window.ftCollectInventoryIds(blob) : [];
            window.ftRerenderOpenSideInfo([casterId, ...invIds]);
        }
    } catch (e) { /* non-fatal */ }
}

window.ftRepaintCasterFromCache = repaintCasterFromCache;

/** Listeners told when a caster_update installed a row that lives in a cached encounter (the canonical row):
 * (casterId, the row, local — true for this browser's own client-truth render). */
export const casterRowInstalled: Array<(casterId: string, row: CasterArr, local: boolean) => void> = [];

function handleCasterUpdate(parsedData: WSCasterUpdateMessage, changedPaths?: readonly string[]): void {
    if (!parsedData.caster_arr) return;
    void window.ftAdoptInventoryBase?.(parsedData.caster_arr);

    // Extract caster (creature) ID — ec id in an encounter, else the standalone PC item_id.
    const casterId = resolveCasterId(parsedData.caster_arr);
    if (!casterId || !checkIfValidUUID(casterId)) return;

    const held = findCasterInEncounterCache(casterId) ?? window.FateTide?.casterCache?.get(casterId);
    const previousEncounterId = held ? String(encounterCreatureField(held.encounter_creatures, 'encounter_id') ?? '') : undefined;
    if (typeof parsedData.version === 'number' && typeof held?._ws_version === 'number'
        && parsedData.version < held._ws_version) return;

    // Encounter members repainted locally have no individual wire stamp. Check every wire
    // snapshot and any explicit local stamp, including malformed or incompatible versions.
    if (parsedData.local !== true || parsedData.caster_arr._meta !== undefined) {
        ftWarnIfShapeStale(parsedData.caster_arr, parsedData.local === true
            ? 'caster_update local repaint' : 'caster_update WS push');
    }

    // STORE — encounter-canonical first (encounter-arr-canonical-pivot): when this creature is
    // attached to a cached FULL encounter blob, encounter_creatures_arr[i] is THE store; the
    // pushed content is synced INTO that row in place (identity preserved for every alias), never
    // installed as a competing copy that overrides the encounter's table data. Only a creature
    // with no cached encounter (standalone PC, out-of-encounter) takes the whole-replace path.
    const canonicalRow = adoptCasterBlobIntoEncounterRow(casterId, parsedData.caster_arr);
    const casterBlob = canonicalRow ?? parsedData.caster_arr;
    if (!canonicalRow) {
        // Standalone (out-of-encounter) whole-row push: carry the prior cached row's seed-correct
        // permissions forward, since the broadcast row is stripped. [unified-viewer-scoping Chunk 2]
        // …including the caster's nested inventory permissions, also stripped on the wire. [Chunk 3]
        const prevRow = window.FateTide?.casterCache?.get(casterId);
        ftCarryPermissionsForward(casterBlob, prevRow?.permissions);
        if (prevRow) ftCarryInventoryPermissionsForward(casterBlob, prevRow);
        window.FateTide?.casterCache?.set(casterId, casterBlob);
    }

    // Stamp the wire version (gap-detector baseline for caster_update_delta) onto the LIVE store
    // object. Absent on legacy/un-versioned full pushes — the delta handler tolerates a missing
    // baseline. [[project_surgical_ws_delta]]
    if (typeof parsedData.version === 'number') {
        (casterBlob as Record<string, unknown>)._ws_version = parsedData.version;
    }
    ftDebugLog('caster', { cacheUpdated: casterId, encounterCanonical: !!canonicalRow });
    // R1 (2026-09-07): a legacy whole-row install is saved truth for the client-truth adapters; they subscribe
    // here (movement-truth.ts) instead of importing this module's importer. `local` = our own render, skipped.
    if (canonicalRow) {
        for (const listener of casterRowInstalled) {
            try { listener(casterId, canonicalRow, parsedData.local === true); } catch (e) { /* observer only */ }
        }
    }

    applyCasterSideEffects(casterBlob, casterId, changedPaths, previousEncounterId);

    // Subscribe glue: re-render the open side-info drawer card IN PLACE if it shows this caster or
    // one of the inventory instances it carries (e.g. a container the user has open and just dragged
    // into). Cache-only — object-card-eligible items/inventory repaint from the just-cached blob with
    // zero network; creatures/PCs/status keep their own live mechanisms. [[open-side-info subscribe]]
    try {
        // The renderer owns visibility, including the FT3D Workspace host. The retired drawer
        // reports closed there and must not suppress this update.
        if (
            typeof window.ftRerenderOpenSideInfo === 'function'
        ) {
            const invIds = (!changedPaths || !casterScalarPaths(changedPaths)) && typeof window.ftCollectInventoryIds === 'function'
                ? window.ftCollectInventoryIds(casterBlob)
                : [];
            window.ftRerenderOpenSideInfo([casterId, ...invIds]);
        }
    } catch (e) { /* non-fatal */ }

    // Live-stock glue: an open trade/trainer panel reloads its fragment when one of its two
    // participants changed. No-op (one string compare) when no panel is open.
    try { window.ftExchangeOnParticipantUpdate?.(casterId); } catch (e) { /* non-fatal */ }
}

export {
    ftApplyEncounterCreatureVisualsFromCasterSnapshot,
    handleCasterUpdate,
    applyCasterSideEffects,
    recoverActiveViewerCasterAfterEncounterDeparture,
    repaintCasterFromCache,
    resolveAppliedActiveEncounterId,
    resolveCasterId,
};
